import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MessageCircle, Phone, MessageSquare } from "lucide-react";

export default function NotificationPrefsCard() {
  const { user, setUser } = useAuth();
  const [waOptin, setWaOptin] = useState(user?.whatsapp_optin ?? true);
  const [smsOptin, setSmsOptin] = useState(user?.sms_optin ?? true);
  const [mobile, setMobile] = useState(user?.mobile || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setWaOptin(user?.whatsapp_optin ?? true);
    setSmsOptin(user?.sms_optin ?? true);
    setMobile(user?.mobile || "");
  }, [user]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/profile/notifications", {
        whatsapp_optin: waOptin,
        sms_optin: smsOptin,
        mobile,
      });
      setUser({ ...user, whatsapp_optin: waOptin, sms_optin: smsOptin, mobile });
      toast.success("Notification preferences saved");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const roleHint =
    user?.role === "worker"
      ? "Get pinged when a new job matches your skills."
      : "Get pinged when a worker applies to your job.";

  return (
    <div className="sarthi-card p-6" data-testid="notif-prefs-card">
      <div className="mb-3 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-500 text-white">
          <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
        </div>
        <div>
          <div className="font-display text-lg font-extrabold text-sarthi-text">Job alerts</div>
          <div className="text-sm font-medium text-sarthi-muted">{roleHint}</div>
        </div>
      </div>

      <label className="mt-4 flex cursor-pointer items-center justify-between rounded-xl border-2 border-sarthi-border bg-sarthi-accent/40 p-4">
        <span className="flex items-center gap-2 font-bold text-sarthi-text">
          <MessageCircle className="h-4 w-4 text-green-600" /> WhatsApp alerts
        </span>
        <input
          type="checkbox"
          data-testid="notif-optin-toggle"
          checked={waOptin}
          onChange={(e) => setWaOptin(e.target.checked)}
          className="h-6 w-6"
        />
      </label>

      <label className="mt-3 flex cursor-pointer items-center justify-between rounded-xl border-2 border-sarthi-border bg-sarthi-accent/40 p-4">
        <span className="flex items-center gap-2 font-bold text-sarthi-text">
          <MessageSquare className="h-4 w-4 text-blue-600" /> SMS fallback
          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase text-blue-700">
            if WhatsApp unavailable
          </span>
        </span>
        <input
          type="checkbox"
          data-testid="notif-sms-toggle"
          checked={smsOptin}
          onChange={(e) => setSmsOptin(e.target.checked)}
          className="h-6 w-6"
        />
      </label>

      <div className="mt-4">
        <label className="mb-1 flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-sarthi-muted">
          <Phone className="h-4 w-4" /> Mobile number
        </label>
        <input
          data-testid="notif-mobile-input"
          value={mobile}
          onChange={(e) => setMobile(e.target.value.replace(/[^\d+]/g, ""))}
          placeholder="9999900001"
          inputMode="tel"
          className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-3 text-base font-semibold focus:border-sarthi-primary focus:outline-none"
        />
        <p className="mt-1 text-xs text-sarthi-muted">
          Used for both WhatsApp and SMS. We'll deliver via WhatsApp first, then SMS if that fails.
        </p>
      </div>

      <button
        data-testid="notif-save-btn"
        onClick={save}
        disabled={busy}
        className="sarthi-btn-primary mt-4 w-full py-3 text-white disabled:opacity-50"
      >
        Save preferences
      </button>
    </div>
  );
}
